import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FullScreenLayout from "../../../components/FullScreenLayout";
import FileDropzone from "../../../components/ui/FileDropzone";
import { BASE_URL, requestOptionsGET } from "../../../utils/Constants";
import { BucketFolder, PdfType } from "../../../utils/Enums";
import { Patient } from "../../../utils/api-objects/Patient";
import { getAllExamsByType } from "../../../utils/apis/emr-service";
import { useAuth } from "../../../utils/context/AuthContext";
import { usePatients } from "../../../utils/context/PatientsContext";
import PdfCard from "../components/PdfCard";
import { PdfExam } from "../components/PdfExam";

const SharedFromEquipe = () => {
    const { auth } = useAuth()
    const role = auth.role
    const idToken = auth.idToken
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patient_id");
    const { patientsState } = usePatients()
    const patients = patientsState.patients

    const [patient, setPatient] = useState<Patient | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    const [uploadedFile, setUploadedFile] = useState<File>()

    const navigate = useNavigate()

    const onFileSelect = (file: File) => {
        setUploadedFile(file)
    }

    const onFileRemove = () => {
        setUploadedFile(undefined)
    }

    const [triggerRefresh, setTriggerRefresh] = useState(false)

    const [files, setFiles] = useState<PdfExam[]>()

    console.log(files)

    useEffect(() => {
        if (!patients) {
            setIsLoading(false);
            return;
        }
        const foundPatient = patients.find((p) => p.user_id === patientId);
        if (!foundPatient) return
        setPatient(foundPatient);

        if (!patientId || !idToken) return

        setIsLoading(false)

    }, [patients, patientId])

    useEffect(() => {
        if (!patientId || !idToken) return
        getAllExamsByType(
            patientId,
            idToken,
            BucketFolder.UploadedFromExpert,
            (exams) => { 
                // Filter out private documents from shared documents
                const sharedExams = exams.filter(exam => {
                    const filePath = exam.url || '';
                    // Exclude files from private folders
                    const isPrivate = filePath.includes('from_expert_private_') || 
                                    filePath.includes('private_expert_');
                    return !isPrivate;
                });
                setFiles(sharedExams);
            },
            (error) => { console.log(error) },
        )
    }, [triggerRefresh])

    if (isLoading) {
        return (
            <FullScreenLayout title="Caricamento...">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div>
                        Attendi un attimo... <br />
                        Stiamo caricando i dati del paziente
                    </div>
                </div>
            </FullScreenLayout>
        );
    }

    if (!patient) {
        return (
            <FullScreenLayout title="Paziente non trovato">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div>
                        Informazioni sul cliente non trovate
                    </div>
                    <button
                        onClick={() => navigate("/platform-experts/customers")}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        Torna a clienti
                    </button>
                </div>
            </FullScreenLayout>
        );
    }

    const onUpload = async () => {
        if (!idToken || !uploadedFile) return
        setIsUploading(true)
        const filename = uploadedFile.name;
        const response = await fetch(`${BASE_URL}/api/health-plans/user/${patientId}/upload-url?filename=${filename}&folder=from_expert`, requestOptionsGET(idToken))
        if (response.ok) {
            const urlResponse = await response.json()
            console.log(urlResponse)
            const formData = new FormData()
            Object.entries(urlResponse.fields).forEach(([k, v]) => {
                formData.append(k, v as unknown as string)
            })
            formData.append('file', uploadedFile)
            const s3response = await axios.post(urlResponse.url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            if (s3response.status === 204) {
                console.log("File uploaded successfully")
                setTriggerRefresh(true)
                setUploadedFile(undefined)
            } else {
                console.log("File not uploaded")
            }
        } else {
            const responseJson = await response.json()
            console.log(responseJson)
        }
        setIsUploading(false)
    }

    return (
        <FullScreenLayout
            title={`${patient.user_name} ${patient.user_family_name}`}
            backButtonText={`Torna a ${patient.user_name} ${patient.user_family_name}`}
            onBack={() => navigate(`/platform-experts/customers/single-customer?patient_id=${patientId}`)}
            showBackButton={true}
        >
            <div className="w-full flex flex-col space-y-6">
                {/* Subtitle */}
                <div className="font-bold text-[28px] md:text-[35px] leading-[35px] text-gray-900">
                    Caricati dall'Equipe Medica
                </div>
                
                {/* File Dropzone */}
                <div className="w-full max-w-2xl">
                    <FileDropzone
                        onFileSelect={onFileSelect}
                        onFileRemove={onFileRemove}
                        onUpload={onUpload}
                        selectedFile={uploadedFile}
                        uploadButtonText="Carica file"
                        selectButtonText="Scegli file"
                        placeholder="Trascina qui un file PDF o immagine, oppure clicca per selezionarlo"
                        isLoading={isUploading}
                    />
                </div>
                
                {/* Existing files */}
                <div className="w-full">
                    <div className="flex flex-wrap gap-4">
                        {
                            files && files.length > 0 && files.map((exam, index) => (
                                <PdfCard key={index} exam={exam} pdfType={PdfType.Uploads} />
                            ))
                        }
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    )
}

export default SharedFromEquipe
